import { queryOptions, skipToken } from "@tanstack/react-query";

import { MASTER_INVALIDATION_KEY } from "@/api/queryClient";
import {
  getDeviceDataTriggerCurrentValue,
  countDeviceEventsTotal,
  getDeviceDataTrigger,
  getDeviceDataTriggersBySearch,
  getPaginatedDeviceDataTriggers,
} from "@/api/resources/deviceDataTriggers/api";
import i18n from "@/providers/i18n";

import type { DatumTriggerTargetTable } from "@/supabase.types";
import type { SearchOptionFilters } from "@/utils/api";
import type { QueryPaginationFilters } from "@/utils/pagination";

const entity = i18n.t("device-data-trigger");
const entityErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entity }),
};
const entities = i18n.t("device-data-triggers.entity");
const entitiesErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entities }),
};

export const deviceDataTriggersQueryKeys = {
  all: () => [...MASTER_INVALIDATION_KEY, "device_data_triggers"],
  lists: () => [...deviceDataTriggersQueryKeys.all(), "list"],
  listPaginated: (queryKeyPaginationFilters: QueryPaginationFilters) => [
    ...deviceDataTriggersQueryKeys.lists(),
    "paginated",
    queryKeyPaginationFilters,
  ],
  listBySearch: (searchOptionFilters: SearchOptionFilters) => [
    ...deviceDataTriggersQueryKeys.lists(),
    "bySearch",
    searchOptionFilters,
  ],
  byId: (id?: string) => [...deviceDataTriggersQueryKeys.all(), "byId", id],
  computeValueById: (id?: string) => [...deviceDataTriggersQueryKeys.all(), "computeValueById", id],
  countDeviceEventsTotalByIdAndTable: (deviceId?: string, table?: DatumTriggerTargetTable) => [
    ...deviceDataTriggersQueryKeys.all(),
    "countDeviceEventsTotalByIdAndTable",
    deviceId,
    table,
  ],
};

export const devicesDataTriggerQueries = {
  listPaginated: (queryKeyPaginationFilters: QueryPaginationFilters) =>
    queryOptions({
      queryKey: [...deviceDataTriggersQueryKeys.listPaginated(queryKeyPaginationFilters)],
      queryFn: () => getPaginatedDeviceDataTriggers(queryKeyPaginationFilters),

      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  listBySearch: (searchOptionFilters: SearchOptionFilters) =>
    queryOptions({
      queryKey: [...deviceDataTriggersQueryKeys.listBySearch(searchOptionFilters)],
      queryFn: () => getDeviceDataTriggersBySearch(searchOptionFilters),
      placeholderData: (prev) => prev, // ! this prevents flashing of results when using search in selectors
      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  byId: (id?: string) =>
    queryOptions({
      queryKey: [...deviceDataTriggersQueryKeys.byId(id)],
      queryFn: id ? () => getDeviceDataTrigger({ match: { id } }) : skipToken,

      meta: {
        errorMessage: entityErrorMessage,
      },
    }),
  computeValueById: (id?: string) =>
    queryOptions({
      queryKey: [...deviceDataTriggersQueryKeys.computeValueById(id)],
      queryFn: id ? () => getDeviceDataTriggerCurrentValue({ id }) : skipToken,

      meta: {
        errorMessage: entityErrorMessage,
      },
    }),
  countDeviceEventsTotalByIdAndTable: (device_id?: string, table?: DatumTriggerTargetTable) =>
    queryOptions({
      queryKey: [...deviceDataTriggersQueryKeys.countDeviceEventsTotalByIdAndTable(device_id, table)],
      queryFn:
        device_id && table ? () => countDeviceEventsTotal({ _device_id: device_id, _target_table: table }) : skipToken,
      meta: {
        errorMessage: entityErrorMessage,
      },
    }),
};
